import { Frame, FrameOptionConsolidated } from '../../../../types/Products';
import {
  getFramePrice,
  getStylePrice,
  parseVariantTitle,
  STANDARD_MOUNTING,
  sumVariants,
} from './framePricing';

export type FramingChangePart = 'frame' | 'glazing' | 'mounting';

export interface FramingChange {
  part: FramingChangePart;
  from: string | null;
  to: string | null;
  priceDelta: number;
}

type FramePart = { name: string | null; price: number };

type FrameParts = Record<FramingChangePart, FramePart>;

const PARTS: FramingChangePart[] = ['frame', 'glazing', 'mounting'];

const EMPTY_PARTS: FrameParts = {
  frame: { name: null, price: 0 },
  glazing: { name: null, price: 0 },
  mounting: { name: null, price: 0 },
};

const cheapestGlazingPrice = (
  option: FrameOptionConsolidated,
  glazing: string,
): number => {
  const prices = option.variants
    .filter((variant) => {
      return parseVariantTitle(variant.title).glazing === glazing;
    })
    .map((variant) => {
      return sumVariants(variant.consolidatedVariants);
    });

  return prices.length ? Math.min(...prices) : 0;
};

const toFrameParts = (
  frameOptions: FrameOptionConsolidated[],
  frame: Frame | null,
): FrameParts => {
  if (!frame) return EMPTY_PARTS;

  const { glazing, mounting } = parseVariantTitle(frame.variant.title);
  const mountingName = mounting === STANDARD_MOUNTING ? null : mounting;
  const price = getFramePrice(frame);
  const option = frameOptions.find(({ title }) => {
    return title === frame.title;
  });

  if (!option) {
    return {
      frame: { name: frame.title, price },
      glazing: { name: glazing, price: 0 },
      mounting: { name: mountingName, price: 0 },
    };
  }

  const stylePrice = getStylePrice(option);
  const glazingPrice = cheapestGlazingPrice(option, glazing);

  return {
    frame: { name: frame.title, price: stylePrice },
    glazing: { name: glazing, price: glazingPrice - stylePrice },
    mounting: { name: mountingName, price: price - glazingPrice },
  };
};

export const getFramingChanges = (
  frameOptions: FrameOptionConsolidated[],
  from: Frame | null,
  to: Frame | null,
): FramingChange[] => {
  const fromParts = toFrameParts(frameOptions, from);
  const toParts = toFrameParts(frameOptions, to);

  return PARTS.flatMap((part) => {
    const change: FramingChange = {
      part,
      from: fromParts[part].name,
      to: toParts[part].name,
      priceDelta: toParts[part].price - fromParts[part].price,
    };

    return change.from === change.to && change.priceDelta === 0 ? [] : [change];
  });
};
